import React, { useMemo } from "react";
import makeStyles from "@material-ui/styles/makeStyles";
import moment from "moment-timezone";
import Button from "@material-ui/core/Button";
import AddIcon from "@material-ui/icons/Add";
import OutlinedInput from "@material-ui/core/OutlinedInput";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import Grid from "@material-ui/core/Grid";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Search from "@material-ui/icons/Search";
import Close from "@material-ui/icons/Close";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Checkbox from "@material-ui/core/Checkbox";
import ListItemText from "@material-ui/core/ListItemText";
import ClearIcon from "@material-ui/icons/Clear";
import { KeyboardDatePicker } from "@material-ui/pickers";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import EligibilityCriteria from "./ElegibilityCriteria";

import enumHelper from "../../../support/logic/enumHelper";
import { EventDepartmentType, EventStatus } from "../../enums";

const useStyles = makeStyles((theme) => ({
    listHeader: {
        marginBottom: 2,
        display: "flex",
        flexDirection: "column",
    },
    search: {
        marginRight: 1,
    },
    selectEmpty: {
        marginTop: 2,
    },
}));

const GRADE_OPTIONS = (() => {
    const options = [];
    for (let i = 5; i <= 21; i++) {
        options.push(i);
    }
    return options;
})();

const ListHeader = ({
    onAdd,
    seasons,
    isActive,
    searchText,
    onSearchTextChange,
    department,
    onDepartmentChange,
    status,
    onStatusChange,
    seasonId,
    onSeasonIdChange,
    grades,
    onGradesChange,
    districts,
    districtList,
    onDistrictsChange,
    zipCodes,
    onZipCodesChange,
    setEligibilityCriteria,
    eligibilityCriteria,
    datePreset,
    onDatePresetChange,
    startDate,
    onStartDateChange,
    endDate,
    onEndDateChange,
    filtersApplied,
    onClearFilters,
}) => {
    const classes = useStyles();

    const departmentOptions = useMemo(() => {
        return enumHelper.getSelectOptionsList(EventDepartmentType, false, "All Departments");
    }, []);

    const statusOptions = useMemo(() => {
        const allStatuses = enumHelper.getSelectOptionsList(EventStatus, false, "All Statuses");

        if (isActive) {
            return allStatuses.filter((s) => s.value !== EventStatus.Cancelled && s.value !== EventStatus.Closed);
        } else {
            return allStatuses.filter((s) => s.value === EventStatus.Cancelled || s.value === EventStatus.Closed || s.value === "");
        }
    }, [isActive]);

    const datePresetOptions = useMemo(() => {
        const currentYear = moment.tz("America/New_York").year();
        return [
            { value: "", label: "All Dates" },
            { value: "custom", label: "Custom Range", disabled: true },
            { value: "previousQuarter", label: "Previous Quarter" },
            { value: "currentQuarter", label: "Current Quarter" },
            { value: "nextQuarter", label: "Next Quarter" },
            { value: `q1-${currentYear}`, label: `Q1 ${currentYear}` },
            { value: `q2-${currentYear}`, label: `Q2 ${currentYear}` },
            { value: `q3-${currentYear}`, label: `Q3 ${currentYear}` },
            { value: `q4-${currentYear}`, label: `Q4 ${currentYear}` },
        ];
    }, []);

    return (
        <div className={classes.listHeader}>
            <Grid container alignItems="center" spacing={2} style={{ paddingBottom: 10 }}>
                <Grid item xs={2}>
                    <OutlinedInput
                        className={classes.search}
                        onChange={(e) => onSearchTextChange(e.target.value)}
                        placeholder="Search by name..."
                        margin="dense"
                        fullWidth
                        startAdornment={<Search />}
                        endAdornment={
                            <InputAdornment position="end">
                                {searchText ? (
                                    <IconButton onClick={() => onSearchTextChange("")} edge="end" size="small">
                                        <Close />
                                    </IconButton>
                                ) : (
                                    <div style={{ width: 27 }} />
                                )}
                            </InputAdornment>
                        }
                        value={searchText}
                    />
                </Grid>
                <Grid item xs={2}>
                    <FormControl fullWidth>
                        <InputLabel shrink id="season-label">
                            Season
                        </InputLabel>
                        <Select
                            labelId="season-label"
                            id="season"
                            margin="dense"
                            value={seasonId}
                            onChange={(e) => onSeasonIdChange(e.target.value)}
                            displayEmpty
                            fullWidth
                        >
                            <MenuItem key="123" value="">
                                All Seasons
                            </MenuItem>
                            {seasons.map((season) => (
                                <MenuItem key={season.id} value={season.id}>
                                    {season.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={2}>
                    <FormControl fullWidth>
                        <InputLabel shrink id="department-label">
                            Department
                        </InputLabel>
                        <Select
                            margin="dense"
                            labelId="department-label"
                            id="department"
                            value={department}
                            onChange={(e) => onDepartmentChange(e.target.value)}
                            displayEmpty
                            fullWidth
                            className={classes.selectEmpty}
                        >
                            {departmentOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={2}>
                    <FormControl fullWidth>
                        <InputLabel shrink id="status-label">
                            Status
                        </InputLabel>
                        <Select
                            labelId="status-label"
                            id="status"
                            margin="dense"
                            value={status}
                            onChange={(e) => onStatusChange(e.target.value)}
                            displayEmpty
                            fullWidth
                        >
                            {statusOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={2}>
                    <FormControl fullWidth>
                        <InputLabel shrink id="grade-label">
                            Grade Level
                        </InputLabel>
                        <Select
                            labelId="grade-label"
                            id="grade"
                            multiple
                            margin="dense"
                            value={grades}
                            onChange={(e) => onGradesChange(e.target.value)}
                            displayEmpty
                            fullWidth
                            renderValue={(selected) => {
                                if (selected.length === 0) return "All Grades";
                                if (selected.length === 1) return `Grade ${selected[0]}`;
                                return `${selected.length} selected`;
                            }}
                        >
                            {GRADE_OPTIONS.map((grade) => (
                                <MenuItem key={grade} value={grade}>
                                    <Checkbox checked={grades.indexOf(grade) > -1} />
                                    <ListItemText primary={grade} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={2}>
                    <FormControl fullWidth>
                        <InputLabel shrink id="district-label">
                            District
                        </InputLabel>
                        <Select
                            labelId="district-label"
                            id="district"
                            multiple
                            margin="dense"
                            value={districts}
                            onChange={(e) => onDistrictsChange(e.target.value)}
                            displayEmpty
                            fullWidth
                            renderValue={(selected) => {
                                if (selected.length === 0) return "All Districts";
                                if (selected.length === 1) {
                                    const match = districtList.find((d) => d.id === selected[0]);
                                    return match ? match.name : selected[0];
                                }
                                return `${selected.length} selected`;
                            }}
                        >
                            {districtList.map((district) => (
                                <MenuItem key={district.id} value={district.id}>
                                    <Checkbox checked={districts.indexOf(district.id) > -1} />
                                    <ListItemText primary={district.name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>
            <Grid container alignItems="center" spacing={2} style={{ paddingBottom: 10 }}>
                <Grid item xs={2}>
                    <FormControl fullWidth>
                        <InputLabel shrink id="date-preset-label">
                            Date Range
                        </InputLabel>
                        <Select
                            labelId="date-preset-label"
                            id="date-preset"
                            margin="dense"
                            value={datePreset}
                            onChange={(e) => onDatePresetChange(e.target.value)}
                            displayEmpty
                            fullWidth
                            className={classes.selectEmpty}
                        >
                            {datePresetOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={2}>
                    <KeyboardDatePicker
                        autoOk
                        clearable
                        variant="inline"
                        inputVariant="outlined"
                        margin="dense"
                        label="Start Date"
                        format="MM/DD/YY"
                        value={startDate}
                        onChange={onStartDateChange}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={2}>
                    <KeyboardDatePicker
                        autoOk
                        clearable
                        variant="inline"
                        inputVariant="outlined"
                        margin="dense"
                        label="End Date"
                        format="MM/DD/YY"
                        value={endDate}
                        minDate={startDate || undefined}
                        onChange={onEndDateChange}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={3}>
                    <Autocomplete
                        multiple
                        freeSolo
                        fullWidth
                        options={[]}
                        value={zipCodes || []}
                        onChange={(_, newValue) => onZipCodesChange(newValue)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="standard"
                                label="Eligible Student Zip Code"
                                placeholder={(zipCodes || []).length === 0 ? "Add ZIP and press Enter" : ""}
                                InputLabelProps={{ shrink: true }}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={1}>
                    <EligibilityCriteria
                        eligibilityCriteria={eligibilityCriteria}
                        setEligibilityCriteria={setEligibilityCriteria}
                    />
                </Grid>
                {filtersApplied && (
                    <Grid item xs={2}>
                        <Button
                            size="small"
                            startIcon={<ClearIcon />}
                            onClick={onClearFilters}
                            style={{ textTransform: "none" }}
                        >
                            Clear Filters
                        </Button>
                    </Grid>
                )}
            </Grid>
            <Grid container spacing={2}>
                <Grid item xs={2}>
                    <Button color="primary" variant="contained" startIcon={<AddIcon />} onClick={onAdd}>
                        Add Event
                    </Button>
                </Grid>
            </Grid>
        </div>
    );
};

export default ListHeader;
